"use client";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { Fragment, useMemo, useState } from "react";
import { useTranslations } from "@/i18n/useTranslations";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  KeyRound,
  PenLine,
  Settings,
  MoreVertical,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useSidebarStore } from "@/stores/sidebar-store";
import { useAuthStore } from "@/stores/auth-store";
import { UserRole } from "@comp-sys/shared";
import { useLogout } from "@/hooks/use-logout";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavigation } from "@/hooks/use-navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChangePasswordDialog } from "@/components/users/change-password-dialog";
import { ChangeNameDialog } from "@/components/users/change-name-dialog";
import { BrandLogo } from "@/components/shared/brand-logo";
import { RoleSwitcherMenuItems } from "@/components/auth/role-switcher";
import { useMessageStore } from "@/stores/message-store";

export function Sidebar() {
  const t = useTranslations("nav");
  const tAuth = useTranslations("auth");
  const tUser = useTranslations("user");
  const tUsers = useTranslations("users");
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const { isCollapsed, setCollapsed, isOpen } = useSidebarStore();
  const user = useAuthStore((state) => state.user);
  const { logout, isPending } = useLogout();
  const { navItems } = useNavigation();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Track which groups are expanded. Auto-expand when a child route is active.
  const autoExpandedKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const item of navItems) {
      if (item.children?.some((child) =>
        pathname === child.href || pathname.startsWith(child.href + '/')
      )) {
        keys.add(item.href);
      }
    }
    return keys;
  }, [navItems, pathname]);

  const [manuallyToggled, setManuallyToggled] = useState<Set<string>>(new Set());

  const isGroupExpanded = (href: string) => {
    // If user manually toggled, use that state; otherwise auto-expand if child is active
    if (manuallyToggled.has(href) || manuallyToggled.has(href + ':open')) {
      return manuallyToggled.has(href + ':open');
    }
    return autoExpandedKeys.has(href);
  };

  const toggleGroup = (href: string) => {
    setManuallyToggled((prev) => {
      // Check manual state first (collapsed OR expanded), then fall back to auto-expand
      const hasManualState = prev.has(href) || prev.has(href + ':open');
      const isOpen = hasManualState ? prev.has(href + ':open') : autoExpandedKeys.has(href);
      const next = new Set(prev);
      // Remove any existing toggle for this key
      next.delete(href);
      next.delete(href + ':open');
      // Set the new state
      if (isOpen) {
        next.add(href); // collapsed
      } else {
        next.add(href + ':open'); // expanded
      }
      return next;
    });
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen transition-all duration-300 flex flex-col",
        "bg-overlay/70 backdrop-blur-xl",
        "border-r border-surface",
        isCollapsed ? "w-20" : "w-64",
        !isOpen && "-translate-x-full md:translate-x-0"
      )}
    >
      {/* Logo & Logout */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-border dark:border-border">
        <BrandLogo variant="auto" size="md" collapsed={isCollapsed} linkTo="/" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!isCollapsed)}
          className="hidden md:flex"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-auto space-y-1 p-3">
        {navItems.map((item) => {
          const hasChildren = !!item.children?.length;
          // Exact match OR prefix match (guarded against /dashboard matching all dashboard pages)
          const isActive = pathname === item.href || (item.href !== '/dashboard' && pathname.startsWith(item.href + '/'));
          // When sidebar is collapsed, show parent as active if any child matches
          const isChildRouteActive = isCollapsed && item.children?.some((child) =>
            child.href === item.href
              ? pathname === child.href
              : pathname === child.href || pathname.startsWith(child.href + '/')
          );
          const isHighlighted = isActive || isChildRouteActive;
          const expanded = isGroupExpanded(item.href);

          return (
            <Fragment key={item.href}>
              {hasChildren && !isCollapsed ? (
                // Group header: label toggles expansion, chevron also toggles
                <div
                  className={cn(
                    "relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    isHighlighted
                      ? "bg-gradient-to-r from-primary to-primary-dark text-primary-foreground shadow-card shadow-primary/25"
                      : "text-muted-foreground hover:bg-overlay/50 hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <Link to={item.href} className="font-medium flex-1 min-w-0">
                    {t(item.labelKey)}
                  </Link>
                  <span
                    className="p-0.5 rounded hover:bg-overlay/20 cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGroup(item.href);
                    }}
                  >
                    <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", !expanded && "-rotate-90")} />
                  </span>
                </div>
              ) : (
                // Regular link (no children, or sidebar collapsed)
                <Link
                  to={item.href}
                  className={cn(
                    "relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    isHighlighted
                      ? "bg-gradient-to-r from-primary to-primary-dark text-primary-foreground shadow-card shadow-primary/25"
                      : "text-muted-foreground hover:bg-overlay/50 hover:text-foreground"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  {!isCollapsed && <span className="font-medium">{t(item.labelKey)}</span>}
                  {item.href === "/dashboard/messages" && unreadCount > 0 && (
                    <span className={cn(
                      "min-w-[20px] h-5 flex items-center justify-center rounded-full bg-destructive text-primary-foreground text-[11px] font-bold px-1.5",
                      isCollapsed && "absolute -top-1 -right-1 min-w-[16px] h-4 text-[9px]"
                    )}>
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Link>
              )}
              {/* Children: only visible when group is expanded and sidebar is not collapsed */}
              {!isCollapsed && hasChildren && expanded && (
                <div className="ml-6 space-y-1">
                  {item.children!.map((child) => {
                    const isChildActive = child.href === item.href
                      ? pathname === child.href
                      : pathname === child.href || pathname.startsWith(child.href + '/');
                    return (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-200",
                          isChildActive
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-overlay/30 hover:text-foreground"
                        )}
                      >
                        {child.icon && <child.icon className="h-4 w-4" />}
                        <span>{t(child.labelKey)}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </Fragment>
          );
        })}
      </nav>

      {/* User Card */}
      <div className="border-t border-border dark:border-border p-2">
        {/* User Info */}
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger
            className={cn(
              "flex items-center gap-3 w-full rounded-xl p-3",
              "hover:bg-muted/80",
              "transition-colors duration-200 cursor-pointer",
              isCollapsed && "justify-center"
            )}
          >
            <Avatar className="h-9 w-9">
              <AvatarImage src="/avatar.png" />
              <AvatarFallback>
                {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || user?.phoneNumber?.[0]?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && user && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium truncate">
                  {user.displayName || user.email || user.phoneNumber}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {tUser(`role.${user.activeRole}`)}
                </p>
              </div>
            )}
            {!isCollapsed && (
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-48">
            <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
            <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
              <PenLine className="h-4 w-4" />
              {tUsers("changeName")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
              <KeyRound className="h-4 w-4" />
              {tUsers("changePassword")}
            </DropdownMenuItem>
            {(user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                  <Settings className="h-4 w-4" />
                  {tAuth("logout.settings")}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              disabled={isPending}
              className="text-error-text"
            >
              <LogOut className="h-4 w-4" />
              {tAuth("logout.title")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />

      {/* Change Name Dialog */}
      <ChangeNameDialog
        open={changeNameOpen}
        onOpenChange={setChangeNameOpen}
      />
    </aside>
  );
}
